import classNames from 'classnames';
import {
  useCallback,
  useEffect,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconChevronDown } from '@sorare/core/src/atoms/icons/system/iconChevronDown.ts';
import { iconChevronUp } from '@sorare/core/src/atoms/icons/system/iconChevronUp.ts';
import { BottomPortal } from '@sorare/core/src/atoms/layout/BottomPortal';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useAvailableRarities } from '@sorare/core/src/hooks/my-cards/useAvailableRarities';
import {
  LIFECYCLE,
  useLifecycle,
  type LifecycleEntries,
} from '@sorare/core/src/hooks/useLifecycle';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import { SelectRarity } from '#src/atoms/SelectRarity/index.tsx';
import { MarketShopTabs } from '#src/components/market-home/MarketShopTabs/index.tsx';

const Sticky = styled(Horizontal)`
  position: sticky;
  top: var(--current-stack-height);
  z-index: 10;
  height: 0;

  align-items: flex-start;
  @media ${tabletAndAbove} {
    justify-content: flex-end;
  }
`;

const FullSBHorizontal = styled(SBHorizontal)`
  width: 100%;
`;

const Container = styled.div`
  position: relative;
  --filter-height: 22px;
  --filter-min-width: var(--double-and-a-half-unit);
  display: flex;
  justify-content: center;
  width: 100%;
  min-width: 100%;
  gap: 0;

  @media ${tabletAndAbove} {
    display: flex;
    width: unset;
    min-width: unset;
    justify-content: flex-start;
    gap: var(--unit);
    padding: var(--double-unit) 0;
  }
`;

const CompetitionFilter = styled(Horizontal)`
  justify-self: flex-end;
  @media ${tabletAndAbove} {
    justify-self: flex-start;
    padding: 0;
    position: static;
    right: unset;
    bottom: unset;
  }
`;

const RarityFilter = styled(Horizontal)`
  z-index: 1;
  & ul {
    --bar-bg-color: var(--c-black);
  }
`;

const CompetitionLogo = styled.img`
  height: var(--filter-height);
  width: 100%;
  object-fit: contain;
  .selected & {
    filter: grayscale(1) invert(1);
  }
`;

const Option = styled(SBHorizontal)`
  cursor: pointer;
  gap: var(--unit);
  min-width: 280px;
  width: 100%;
  max-width: 100%;
  padding: var(--unit) var(--intermediate-unit);
  transition: 0.2s ease-in-out;
  &:hover {
    background-color: var(--c-nd-50);
  }
  &.is-selected {
    background-color: var(--c-nd-50);
  }
  @media ${tabletAndAbove} {
    min-width: 320px;
  }
`;

const Icon = styled.div`
  flex-shrink: 0;
  > * {
    width: var(--double-and-a-half-unit);
  }
`;

const shieldIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--triple-unit);
  height: var(--triple-unit);
  > * {
    max-width: 100%;
    max-height: 100%;
  }
`;

type PickerOption<T extends string> = {
  label: ReactNode;
  selectedLabel: ReactNode;
  value: T;
};

type PickerOptionsProps<T extends string> = {
  options: PickerOption<T>[];
  value: T;
  onChange: (value: T) => void;
  closeDropdown: () => void;
};
const PickerOptions = <T extends string>({
  options,
  value,
  onChange,
  closeDropdown,
}: PickerOptionsProps<T>) => (
  <>
    {options.map(option => (
      <Option
        className={classNames({ 'is-selected': option.value === value })}
        key={option.value}
        as={ButtonBase}
        onClick={() => {
          closeDropdown();
          onChange(option.value);
        }}
      >
        <Horizontal as="span" gap={1.5}>
          <Icon>{option.label}</Icon>
          <LabelL color="var(--c-white)">{option.selectedLabel}</LabelL>
        </Horizontal>
        {option.value === value && <FontAwesomeIcon icon={iconCheck} />}
      </Option>
    ))}
  </>
);

type ShieldPickerProps<T extends string> = {
  ariaLabel: string;
  options: PickerOption<T>[];
  value: T;
  onChange: (value: T) => void;
  orientation?: ComponentProps<typeof ShieldButton>['orientation'];
};
const ShieldPicker = <T extends string>({
  ariaLabel,
  options,
  value,
  onChange,
  orientation = 'down',
}: ShieldPickerProps<T>) => {
  const selectedOption = options.find(option => option.value === value);

  return (
    <Dropdown
      gap={4}
      label={({ open, ...btnProps }) => (
        <ShieldButton
          type="button"
          aria-label={ariaLabel}
          size="sm"
          orientation={orientation}
          icon={<span className={shieldIcon}>{selectedOption?.label}</span>}
          {...btnProps}
        />
      )}
    >
      {({ closeDropdown }) => (
        <PickerOptions
          options={options}
          value={value}
          onChange={onChange}
          closeDropdown={closeDropdown}
        />
      )}
    </Dropdown>
  );
};

type SwitchCompetitionProps = {
  competitionSlug: string;
  onChangeCompetition: (c: string) => void;
  options: PickerOption<string>[];
};
const SwitchCompetition = ({
  competitionSlug,
  onChangeCompetition,
  options,
}: SwitchCompetitionProps) => {
  const selectedOption = options.find(
    option => option.value === competitionSlug
  );

  return (
    <Dropdown
      gap={4}
      label={({ open, ...btnProps }) => (
        <Button
          color="tertiary"
          size="small"
          className={classNames({ active: open })}
          endIcon={
            <FontAwesomeIcon icon={open ? iconChevronUp : iconChevronDown} />
          }
          {...btnProps}
        >
          <Icon>{selectedOption?.label}</Icon>
          <LabelL>{selectedOption?.selectedLabel}</LabelL>
        </Button>
      )}
    >
      {({ closeDropdown }) => (
        <PickerOptions
          options={options}
          value={competitionSlug}
          onChange={onChangeCompetition}
          closeDropdown={closeDropdown}
        />
      )}
    </Dropdown>
  );
};

const rarityMessages = {
  ...scarcityMessages,
  [Rarity.common]: navLabels.set,
};

type MarketHomeFilter = NonNullable<
  LifecycleEntries['marketHomeFilters']
>[Sport];

export const Filters = ({ sport }: { sport: Sport }) => {
  const { formatMessage } = useIntl();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { scoutableSo5Competitions } = useSo5Competitions();
  const [searchParams, setSearchParams] = useSearchParams();
  const buyGemsParam = searchParams.get('buy_gems');
  const rarityParam = searchParams.get('rarity');

  const { lifecycle, update: updateLifecycle } = useLifecycle();

  const availableRarities = useAvailableRarities();

  const rarity = (() => {
    if (availableRarities.length === 1) {
      return availableRarities[0];
    }
    if (
      lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.rarity &&
      lifecycle[LIFECYCLE.marketHomeFilters][sport].rarity !==
        Rarity.custom_series &&
      availableRarities.includes(
        lifecycle[LIFECYCLE.marketHomeFilters][sport].rarity
      )
    ) {
      return lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.rarity;
    }
    return Rarity.limited;
  })();

  const selectedCompetitionSlug =
    lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.competitionSlug ||
    'DEFAULT';

  const updateMarketHomeFilters = useCallback(
    (newFilters: Partial<MarketHomeFilter>) => {
      updateLifecycle(LIFECYCLE.marketHomeFilters, {
        ...lifecycle?.[LIFECYCLE.marketHomeFilters],
        [sport]: {
          ...lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport],
          ...newFilters,
        },
      });
    },
    [updateLifecycle, sport, lifecycle]
  );

  const updateRarity = useCallback(
    (r: Rarity) => updateMarketHomeFilters({ rarity: r }),
    [updateMarketHomeFilters]
  );

  const onChangeCompetition = (c: string) => {
    updateMarketHomeFilters({ competitionSlug: c });
  };

  useEffect(() => {
    if (rarityParam === Rarity.common || buyGemsParam === 'true') {
      if (rarity !== Rarity.common) {
        updateRarity(Rarity.common);
      }
    }
    if (rarityParam) {
      setSearchParams(
        prev => {
          prev.delete('rarity');
          return prev;
        },
        {
          replace: true,
        }
      );
    }
  }, [rarityParam, buyGemsParam, rarity, updateRarity, setSearchParams]);

  const competitionOptions = [
    {
      label: <StarBall />,
      selectedLabel: <FormattedMessage defaultMessage="All" />,
      value: 'DEFAULT',
    },
    ...scoutableSo5Competitions.map(competition => ({
      label: (
        <CompetitionLogo
          src={competition.iconUrl}
          alt={competition.displayName}
        />
      ),
      selectedLabel: competition.displayName,
      value: competition.slug,
    })),
  ];

  const onChangeRarity = (r: Rarity) => {
    updateRarity(r);
  };

  const hideCompetitionFilter =
    scoutableSo5Competitions.length < 2 || rarity === Rarity.common;

  const rarities = availableRarities.filter(r =>
    sport === Sport.BASEBALL ? r !== Rarity.common : true
  );
  const raritiesWithBadgeContent = rarities.map(r => ({
    value: r,
    badgeContent: undefined,
  }));
  const rarityOptions = rarities.map(r => ({
    label: <ScarcityIcon rarity={r} size={3} />,
    selectedLabel: <FormattedMessage {...rarityMessages[r]} />,
    value: r,
  }));

  return (
    <Sticky>
      <FullSBHorizontal>
        {!!isTabletAndAbove && <MarketShopTabs rarity={rarity} sport={sport} />}
        <ConditionalWrapper wrap={!isTabletAndAbove} Wrapper={BottomPortal}>
          <Container>
            {isTabletAndAbove ? (
              <>
                {!hideCompetitionFilter && (
                  <CompetitionFilter>
                    <SwitchCompetition
                      competitionSlug={selectedCompetitionSlug}
                      onChangeCompetition={onChangeCompetition}
                      options={competitionOptions}
                    />
                  </CompetitionFilter>
                )}
                <RarityFilter>
                  <SelectRarity
                    rarities={raritiesWithBadgeContent}
                    selectedRarity={rarity}
                    onChange={onChangeRarity}
                    buttonSize="small"
                  />
                </RarityFilter>
              </>
            ) : (
              <>
                <ShieldPicker
                  ariaLabel={formatMessage({ defaultMessage: 'Select rarity' })}
                  orientation="up"
                  options={rarityOptions}
                  value={rarity}
                  onChange={onChangeRarity}
                />
                {!hideCompetitionFilter && (
                  <ShieldPicker
                    ariaLabel={formatMessage({
                      defaultMessage: 'Select competition',
                    })}
                    options={competitionOptions}
                    value={selectedCompetitionSlug}
                    onChange={onChangeCompetition}
                  />
                )}
              </>
            )}
          </Container>
        </ConditionalWrapper>
      </FullSBHorizontal>
    </Sticky>
  );
};
